import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type {
  FinanceSummary,
  FinanceDaysResponse,
  FinanceDayData,
  UpsertPlayerPaymentPayload,
  CreateExpensePayload,
  UpdateExpensePayload,
  CreateTransferPayload,
} from '../types/finance';

export function useFinanceSummary() {
  return useQuery({
    queryKey: ['finance-summary'],
    queryFn: () => api.get<FinanceSummary>('/finance/summary'),
    staleTime: 15_000,
  });
}

export function useFinanceDays() {
  return useQuery({
    queryKey: ['finance-days'],
    queryFn: () => api.get<FinanceDaysResponse>('/finance/days'),
  });
}

export function useFinanceDay(date: string) {
  return useQuery({
    queryKey: ['finance-day', date],
    queryFn: () => api.get<FinanceDayData>(`/finance/days/${date}`),
    enabled: !!date,
  });
}

export function useUpsertPlayerPayment(date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerKey, payload }: { playerKey: string; payload: UpsertPlayerPaymentPayload }) =>
      api.put(`/finance/days/${date}/payments/${encodeURIComponent(playerKey)}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-day', date] });
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateExpensePayload) =>
      api.post<{ entryId: string; pk: string; sk: string }>('/finance/expenses', payload),
    onSuccess: (_r, vars) => {
      if (vars.type === 'game') {
        queryClient.invalidateQueries({ queryKey: ['finance-day', vars.date] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['finance-days'] });
      }
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

export function useUpdateExpense(date?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: UpdateExpensePayload }) =>
      api.put(`/finance/expenses/${entryId}`, payload),
    onSuccess: () => {
      if (date) queryClient.invalidateQueries({ queryKey: ['finance-day', date] });
      queryClient.invalidateQueries({ queryKey: ['finance-days'] });
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

export function useDeleteExpense(date?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, pk, sk }: { entryId: string; pk: string; sk: string }) =>
      api.delete(`/finance/expenses/${entryId}?pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`),
    onSuccess: () => {
      if (date) queryClient.invalidateQueries({ queryKey: ['finance-day', date] });
      queryClient.invalidateQueries({ queryKey: ['finance-days'] });
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

export function useCreateTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTransferPayload) =>
      api.post('/finance/transfers', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
      queryClient.invalidateQueries({ queryKey: ['finance-days'] });
    },
  });
}
